// eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
import {type NodeTypes, parse, type TemplateChildNode} from '@vue/compiler-dom';
import {createTextVNode, createVNode, h, type VNode} from 'vue';
import {defu} from 'defu';
import {useSofieConfig} from '../composables/config';
import SofieForm from './SofieForm.vue';
import {ClientOnly} from '#components';

export default defineComponent({
  name: 'SofieText',

  props: {
    value: {
      type: String as PropType<string | null | undefined>,
      required: true,
    },
  },

  setup(props, {attrs}) {
    const config = useSofieConfig();

    /**
     * Transforms a Vue AST node to VDom. Will check text nodes for Sofie mustache references.
     *
     * Supported:
     *  - {{FORM-123}} form embeds
     *
     *  Implementation note: Node types are checked as bare integers as Vue currently does not export this as a readable
     *  enum or map that allows type safe comparison.
     *  @see {NodeTypes}
     */
    function transformASTToVDom(node: TemplateChildNode): VNode[] | VNode {
      if (node.type === 2) { // Text node
        return createTextVNode(node.content);
      }

      if (node.type === 5) { // Interpolation node, due to similar syntax of {{FORM-123}}
        if (node.content.type === 4) { // Simple expression, the FORM-123 segment will register as one
          const matches = node.content.content.match(/FORM-(\d+)/);

          if (matches) {
            return h(ClientOnly, null, () => h(SofieForm, {
              ...config.components?.form ?? {},
              formId: +matches[1],
            }));
          }
        }

        // When no valid matches are found, return the entire segment as a text node to preserve data.
        return createTextVNode(node.loc.source);
      }

      if (node.type !== 1) { // Element node
        return [];
      }

      const attrs = Object.fromEntries(
        node.props?.map(prop => [prop.name, prop.type === 6 ? prop.value?.content : undefined],
        ) ?? []);

      const children = node.children.flatMap(transformASTToVDom);

      // Paragraphs shouldn't contain block children, so deconstruct them into siblings
      if (['P'].includes(node.tag.toUpperCase())) {
        const nodes: VNode[] = [];
        let basicChildren: VNode[] = [];

        for (const child of children) {
          if (typeof child.type === 'object') {
            if (basicChildren.length) {
              nodes.push(createVNode(node.tag, attrs, basicChildren));
            }

            nodes.push(child);

            basicChildren = [];
          } else {
            basicChildren.push(child);
          }
        }

        if (basicChildren.length) {
          nodes.push(createVNode(node.tag, attrs, basicChildren));
        }

        return nodes;
      }

      return createVNode(node.tag, attrs, children);
    }

    return () => h(
      'div',
      defu(attrs, config.components?.text ?? {}),
      props.value
        ? parse(props.value, {
          whitespace: 'condense',
          onError: () => null,
        }).children.flatMap(transformASTToVDom)
        : [],
    );
  },
});
